import moment, { Moment, unitOfTime } from 'moment'
import { loadStudentClassrooms } from '../reducers/student/studentClassroomsSlice'
import { getDetailedLesson, getInitialDailyBeats, getInitialLessons } from '../requests/studentRequests'
import { LessonState, loadDetailedLessonData, loadInitialLessonData } from '../reducers/student/lessonsSlice'
import { loadDailyBeats } from '../reducers/student/dailyBeatsSlice'
import { pullUserFromServer } from '../logic/pullUserFromServer'
import { Dispatch } from '@reduxjs/toolkit'
import type {
  DetailedDailyBeat,
  DetailedLessonData,
  FocusSeshLog,
  FocusSeshUsageData, HandleRequestWithRetryAndBackoffParams,
  StudentClassroom, StudentClassroomRaw,
  UserData,
} from "../types"

export const getMomentDateNow = (): string => moment().format(moment.HTML5_FMT.DATE)

export const getMomentDateTimeNow = (): string => moment().format('YYYY-MM-DD HH:mm:ss')

export const getMomentDateTimeInXUnitsFromNow = (
    quantity: number,
    unit: unitOfTime.DurationConstructor,
): string => moment().add(quantity, unit).format('YYYY-MM-DD HH:mm:ss')

export const getTimeOfDay = (time: Moment): string => {
  const hour = time.hour()

  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

export const remoteImageLoader = ({src}: { src: string }): string => {
  return src
}

export const stringContainsLetter = (string: string): boolean => {
  const regExp = /[a-zA-Z]/g
  return regExp.test(string)
}

export const formatFloatAsPercent = (number: number, decimalDigits: number = 0, capAt100Percent: boolean = false): string => {
  if (isNaN(number)) {
    return `0%`
  }
  if (capAt100Percent) {
    return `${Math.min(100, number * 100).toFixed(decimalDigits)}%`
  } else {
    return `${(number * 100).toFixed(decimalDigits)}%`
  }
}

export const stringContainsNumber = (string: string): boolean => {
  const regExp = /[0-9]/g
  return regExp.test(string)
}

export const momentStartDate = (startDate: string): Moment => {
  return moment(startDate)
}

export const isProd = (): boolean => {
  return process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod'
}

// Validates date is in mm/dd/yyyy format
export const isValidDateString = (dateStr: string): boolean => {
  return !!dateStr && /^[0-9]{1,2}[\/\-][0-9]{1,2}[\/\-]\d{4}$/.test(dateStr)
}


export const validateEmail = (email: string): RegExpMatchArray | null => {
  return String(email)
      .toLowerCase()
      .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )
}

export const sleep = (ms: number): Promise<void> => new Promise((r) => {
  if (process.env.NODE_ENV === 'test') {
    setTimeout(r, 0)
  } else {
    setTimeout(r, ms)
  }
})

export const postMessageToNativeApp = (message: string | Record<string, any>): void => {
  if (window?.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message))
  }
}

export const isInNativeApp = (): boolean => {
  return !!window.ReactNativeWebView
}

export const getRandomFloat = (min: number, max: number, decimals: number): number => {
  const str = (Math.random() * (max - min) + min).toFixed(decimals)

  return parseFloat(str)
}

export const getRandomInt = (min: number, max: number): number => {
  const roundedMin = Math.ceil(min)
  const roundedMax = Math.floor(max)
  return Math.floor(Math.random() * (roundedMax - roundedMin) + roundedMin) // The maximum is exclusive and the minimum is inclusive
}


export const shuffleArray = <T>(array: T[]): T[] => {
  let currentIndex = array.length, randomIndex: number

  // While there remain elements to shuffle.
  while (currentIndex != 0) {

    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]]
  }

  return array
}

export const capitalizeFirstLetterOfEachWord = (string: string): string => {
  if (!string) return ''
  return string.split(" ").map((word) => word.charAt(0).toUpperCase() + word.substring(1)).join(" ")
}

export const getInternalDomainByEnvironment = (): string | undefined => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod') {
    return 'https://portal.findingfocus.app'
  } else if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging') {
    return 'https://beta-portal.findingfocus.app'
  } else if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev') {
    return 'http://localhost'
  }
}

export const getServerDomainByEnvironment = (): string | undefined => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod') {
    return 'https://services.findingfocus.app'
  } else if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging') {
    return 'https://beta-services.findingfocus.app'
  } else if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev') {
    return 'http://localhost:3002'
  }
}

export const getClassLinkClientIdByEnvironment = (): string | undefined => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod') {
    return 'c1766178452196b505419767f2f5b3266076d2abb484'
  } else if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging') {
    return 'c17654829644283a6f6567c6d7a496456b91b3b9c735'
  } else if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev') {
    return 'c1764804761622c5a5875ffcadd60dbaa5fa7a1af4c2'
  }
}

export const getStartOfDayForDate = (date: string): string => {
  return moment(date).startOf('day').toISOString()
}

export const getEndOfDayForDate = (date: string): string => {
  return moment(date).endOf('day').toISOString()
}

export const getTotalFocusSeshTimeInMinutes = (logs: FocusSeshLog[]): number => {
  return Math.round(logs.reduce((sum, log) => log.secondsDuration + sum, 0) / 60)
}

// Returns 'NUM min' if total time is less than 60 mins
// Returns 'NUM hr' rounded to nearest half hour if total time is more than 60 mins
export const getTotalFocusSeshTimeInMinutesOrHours = (logs: FocusSeshLog[]): string => {
  const totalTimeInMinutes = Math.round(logs.reduce((sum: number, log): number => log.secondsDuration + sum, 0) / 60)
  let totalTime: string
  if (totalTimeInMinutes < 60) {
    totalTime = `${totalTimeInMinutes} min`
  } else if (totalTimeInMinutes >= 60) {
    // Calculate total minutes into hours, rounded to the nearest half hour
    const totalTimeInHours = Math.round(totalTimeInMinutes / 30) / 2
    totalTime = `${totalTimeInHours} hr`
  }
  return totalTime
}

// Get data from logs for today, this week, and all time
export const getFocusSeshUsageDataFromLogs = (logs: FocusSeshLog[]): FocusSeshUsageData => {
  const sessions: number = logs.length
  const time = getTotalFocusSeshTimeInMinutesOrHours(logs)
  const completedCheckIns: number = logs.reduce((sum, log) => log.numCheckIns + sum, 0) || 0
  const missedCheckIns: number = logs.reduce((sum, log) => log.numMissedCheckIns + sum, 0) || 0
  return {
    sessions,
    time,
    completedCheckIns,
    missedCheckIns,
  }
}

export const loadStudentClassroomsLessonsAndDailyBeats = async (dispatch: Dispatch): Promise<void> => {
  const userData: UserData = await pullUserFromServer()
  const classrooms: StudentClassroom[] = userData.classrooms.map((classroom: StudentClassroomRaw) => ({
    classroomId: classroom.classroomId,
    completed: classroom.completed,
    isStudent: classroom.isStudent,
    createdAt: classroom.createdAt,
    updatedAt: classroom.updatedAt,
    name: classroom['classroom.name'],
    subject: classroom['classroom.subject'],
    emoji: classroom['classroom.emoji'],
    teacherLastName: classroom['classroom.classroomTeacher.lastName'],
    startDate: classroom['classroom.startDate'],
    defaultSchedule: classroom['classroom.defaultSchedule'],
    inviteCode: classroom['classroom.code'],
    courseVersion: classroom['classroom.course.version'],
    latestCourseVersion: classroom.latestCourseVersion,
    mheCourseVersion: classroom['classroom.mheCourse.version'],
    latestMHECourseVersion: classroom.latestMHECourseVersion,
    archived: classroom['classroom.archived'],
  }))
  if (classrooms.length) {
    dispatch(loadStudentClassrooms(classrooms))
  }

  const { classroomId, courseVersion } = classrooms[0]

  // Load Lessons Data
  // TODO could we update this to just use the course version stored on their user entry? this avoids userClassrooms[0] and need to update that all the time
  const lessonsData: LessonState[] = await getInitialLessons(classroomId)
  dispatch(loadInitialLessonData(lessonsData))
  await Promise.all(lessonsData.map((lesson): Promise<void> =>
    getDetailedLesson(classroomId, lesson.id)
      .then((detailedLesson: DetailedLessonData) => {
        dispatch(loadDetailedLessonData(detailedLesson))
      })
      .catch((e) => e)))

  // TODO: remove if we ever deprecate 10-day version of the course that includes Daily Beats
  if (courseVersion >= 13) {
    dispatch(loadDailyBeats([]))
    return
  }

  // Load Daily Beats Data
  try {
    const dailyBeatsData: DetailedDailyBeat[] = await getInitialDailyBeats(classroomId)
    dispatch(loadDailyBeats(dailyBeatsData))
  } catch (e) {
    dispatch(loadDailyBeats([]))
  }
}

export const handleRequestWithRetryAndBackoff = async ({
  requestFunction,
  errorLogPrefix = 'Error performing request',
  retryCount = 0,
  maxRetries = 2,
  initialBackoffMs = 1000,
  onRetry = (retryCount) => {
    console.log(`Retrying... Attempt ${retryCount + 2} of ${maxRetries + 1}`)
  },
  onFailure = () => {},
}: HandleRequestWithRetryAndBackoffParams): Promise<void> => {
  try {
    return await requestFunction()
  } catch (error: unknown) {
    // @ts-ignore
    console.error(`${errorLogPrefix}: ${error?.message || error}`)

    if (retryCount >= maxRetries) {
      console.error(`Failed after ${maxRetries + 1} attempts`)
      // @ts-ignore
      onFailure(error?.message || 'Unknown error')
      return undefined
    }

    // Calculate exponential backoff time
    const backoffTime = Math.pow(2, retryCount) * initialBackoffMs

    // Call the onRetry callback if provided
    onRetry(retryCount, backoffTime)

    // Wait for the backoff period
    await new Promise(resolve => setTimeout(resolve, backoffTime))

    // Try again recursively with incremented retry count
    return handleRequestWithRetryAndBackoff({
      requestFunction,
      errorLogPrefix,
      maxRetries,
      initialBackoffMs,
      onFailure,
      onRetry,
      retryCount: retryCount + 1,
    })
  }
}

export const loadAudioWithErrorHandling = (url: string): Promise<HTMLAudioElement> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio()

    const onCanPlay = () => {
      cleanup()
      resolve(audio)
    }

    const onError = () => {
      cleanup()
      reject(new Error(`Failed to load audio from ${url}: Unknown error`))
    }

    const cleanup = () => {
      audio.removeEventListener('canplaythrough', onCanPlay)
      audio.removeEventListener('error', onError)
    }

    audio.addEventListener('canplaythrough', onCanPlay)
    audio.addEventListener('error', onError)

    audio.src = url
    audio.load() // Explicitly trigger loading
  })
}

export const SSO_TYPES = {
  GOOGLE: 'Google',
  APPLE: 'Apple',
  CLEVER: 'Clever',
  CLASSLINK: 'ClassLink',
}

export const rolesToTreatAsEducator = ['teacher', 'district_admin', 'school_admin', 'admin']

export const isEducator = (role: string) => rolesToTreatAsEducator.includes(role)

export const getPointsPerLesson = (totalCoursePoints: number, numLessons: number) => {
  if (!totalCoursePoints || !numLessons) {
    return 0
  }

  return totalCoursePoints / numLessons
}

